<script lang="ts">
  /**
   * Modal for ingesting a document into the tenant's NEXUS.
   *
   * Pick a .md/.txt file (or paste text) and submit — POSTs to
   * /ingest/document on DC, which chunks + embeds the text and writes
   * tenant-scoped claude_memory rows. The tenant is resolved server-side
   * from the MCO bearer; the chunks appear in the constellation once
   * embedded. Backs milestone 91418 (the MU Settings ingest button).
   */
  import { writeMode } from './selection.svelte'
  import { mco } from './mcoAuth.svelte'

  const NR_BASE =
    (import.meta as unknown as { env: Record<string, string | undefined> }).env
      .VITE_NR_BASE ?? 'https://red.psyopsys.com'

  let topic = $state('')
  let content = $state('')
  let fileName = $state('')
  let submitting = $state(false)
  let error = $state<string | null>(null)
  let result = $state<{
    status: string
    jobId?: string
    bytes: number
    idempotent: boolean
  } | null>(null)

  function reset(): void {
    topic = ''
    content = ''
    fileName = ''
    error = null
    result = null
    submitting = false
  }

  function fmtSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
  }

  function close(): void {
    writeMode.ingestDialogOpen = false
    reset()
  }

  function onFile(e: Event): void {
    const input = e.target as HTMLInputElement
    const file = input.files?.[0]
    if (!file) return
    fileName = file.name
    if (!topic.trim()) topic = file.name.replace(/\.[^.]+$/, '')
    const reader = new FileReader()
    reader.onload = () => { content = String(reader.result ?? '') }
    reader.onerror = () => { error = 'could not read that file' }
    reader.readAsText(file)
  }

  async function submit(): Promise<void> {
    if (submitting) return
    if (content.trim().length === 0) {
      error = 'pick a file or paste some text'
      return
    }
    const token = mco.token || localStorage.getItem('mco_access_token')
    if (!token) {
      error = 'not signed in to MCO'
      return
    }
    submitting = true
    error = null
    const bytes = new TextEncoder().encode(content).length
    try {
      const r = await fetch(`${NR_BASE}/ingest/document`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text: content, topic: topic.trim() || 'document' }),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok || !j.ok) {
        const detail = j.detail ? ` (${j.detail})` : ''
        throw new Error((j.error || `ingest failed (HTTP ${r.status})`) + detail)
      }
      result = {
        status: String(j.status || 'queued'),
        jobId: typeof j.job_id === 'string' ? j.job_id : undefined,
        bytes,
        idempotent: Boolean(j.idempotent),
      }
    } catch (err) {
      error = (err as Error).message
    } finally {
      submitting = false
    }
  }

  function onKey(e: KeyboardEvent): void {
    if (!writeMode.ingestDialogOpen) return
    if (e.key === 'Escape') {
      e.preventDefault()
      close()
    } else if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      void submit()
    }
  }
</script>

<svelte:window onkeydown={onKey} />

{#if writeMode.ingestDialogOpen}
  <div class="ing-backdrop" role="presentation" onclick={(e) => { if (e.target === e.currentTarget) close() }}>
    <div class="ing-dialog" aria-label="Ingest document">
      <header class="ing-head">
        <span class="ing-tag">// ingest document</span>
        <button class="ing-close" type="button" onclick={close} aria-label="Close">✕</button>
      </header>

      {#if result}
        <div class="ing-body ing-result">
          <div class="ing-row-confirm">
            <span class="ing-confirm-glyph">✓</span>
            <span class="ing-confirm-text">
              {#if result.idempotent}
                ALREADY UPLOADED
              {:else if result.status === 'done'}
                DONE — {fmtSize(result.bytes)}
              {:else if result.status === 'failed'}
                FAILED — see queue
              {:else}
                QUEUED — {fmtSize(result.bytes)}
              {/if}
            </span>
          </div>
          <p class="ing-hint">
            {#if result.idempotent}
              An identical upload is already in the queue or completed. No new
              work was scheduled. Check the queue panel for its current state.
            {:else if result.status === 'done'}
              Embedded and stored. The chunks appear in your constellation
              within a moment.
            {:else}
              Upload accepted and queued. A background worker chunks + embeds
              into your NEXUS — typically takes a few minutes for a multi-MB
              document. Chunks appear in your constellation as each batch
              commits.
            {/if}
          </p>
          {#if result.jobId}
            <p class="ing-jobid">
              <span class="ing-k">job</span>
              <code>{result.jobId}</code>
            </p>
          {/if}
          <div class="ing-actions">
            <button class="ing-btn" type="button" onclick={() => { result = null; reset() }}>+ ANOTHER</button>
            <button class="ing-btn ing-btn-primary" type="button" onclick={close}>DONE</button>
          </div>
        </div>
      {:else}
        <form class="ing-body" onsubmit={(e) => { e.preventDefault(); void submit() }}>
          <label class="ing-field">
            <span class="ing-k">topic</span>
            <input
              class="ing-input"
              type="text"
              bind:value={topic}
              maxlength="200"
              placeholder="optional — defaults to the file name"
              autocomplete="off"
              spellcheck="false"
            />
          </label>

          <label class="ing-field">
            <span class="ing-k">file <span class="ing-sub">(.md / .txt)</span></span>
            <input
              class="ing-input ing-file"
              type="file"
              accept=".md,.markdown,.txt,.text"
              onchange={onFile}
            />
          </label>

          <label class="ing-field">
            <span class="ing-k">content{#if fileName}<span class="ing-sub"> — {fileName}</span>{/if}</span>
            <textarea
              class="ing-input ing-textarea"
              bind:value={content}
              placeholder="…or paste the document text here"
              rows="8"
            ></textarea>
          </label>

          {#if error}
            <div class="ing-error">{error}</div>
          {/if}

          <div class="ing-actions">
            <span class="ing-shortcut">⌘↵ to ingest · ⎋ to close</span>
            <button class="ing-btn" type="button" onclick={close}>CANCEL</button>
            <button class="ing-btn ing-btn-primary" type="submit" disabled={submitting}>
              {submitting ? 'INGESTING…' : 'INGEST'}
            </button>
          </div>
        </form>
      {/if}
    </div>
  </div>
{/if}

<style>
  .ing-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.78);
    backdrop-filter: blur(2px);
    z-index: 200;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 8vh var(--s-3) var(--s-3);
    animation: ing-fade var(--t-mid);
  }
  @keyframes ing-fade { from { opacity: 0 } to { opacity: 1 } }

  .ing-dialog {
    width: 100%;
    max-width: 560px;
    max-height: 80vh;
    background: var(--surface);
    border: 1px solid var(--accent);
    box-shadow: 0 16px 48px rgba(0, 0, 0, 0.7), 0 0 16px var(--accent-faint);
    display: flex;
    flex-direction: column;
    font-family: var(--font-mono);
  }
  .ing-head {
    display: flex;
    align-items: center;
    gap: var(--s-2);
    padding: var(--s-2) var(--s-3);
    border-bottom: 1px solid var(--outline-variant);
    background: var(--surface-container-low);
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    flex-shrink: 0;
  }
  .ing-tag { color: var(--accent); font-weight: 700; flex: 1; }
  .ing-close {
    background: transparent;
    border: 1px solid var(--outline-variant);
    color: var(--on-surface-variant);
    width: 24px; height: 24px;
    cursor: pointer;
    line-height: 1;
  }
  .ing-close:hover { color: var(--accent); border-color: var(--accent); }

  .ing-body {
    display: flex;
    flex-direction: column;
    gap: var(--s-3);
    padding: var(--s-3);
    overflow-y: auto;
    flex: 1;
  }

  .ing-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .ing-k {
    font-size: 10px;
    color: var(--on-surface-faint);
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }
  .ing-sub { color: var(--on-surface-faint); text-transform: none; letter-spacing: 0; }
  .ing-input {
    font-family: inherit;
    font-size: 12px;
    color: var(--on-surface);
    background: var(--surface-container-low);
    border: 1px solid var(--outline-variant);
    padding: 6px 8px;
    outline: none;
    transition: border-color var(--t-fast);
    width: 100%;
  }
  .ing-input:focus { border-color: var(--accent); }
  .ing-file { padding: 4px; cursor: pointer; }
  .ing-textarea {
    font-family: inherit;
    line-height: 1.5;
    resize: vertical;
    min-height: 120px;
  }

  .ing-error {
    color: #ff6b6b;
    font-size: 11px;
    border: 1px dashed #ff6b6b;
    padding: 4px 8px;
    background: rgba(255, 107, 107, 0.05);
  }

  .ing-actions {
    display: flex;
    align-items: center;
    gap: var(--s-2);
    margin-top: var(--s-2);
  }
  .ing-shortcut {
    flex: 1;
    color: var(--on-surface-faint);
    font-size: 9px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  .ing-btn {
    font: inherit;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    padding: 6px 12px;
    background: transparent;
    color: var(--on-surface-variant);
    border: 1px solid var(--outline-variant);
    cursor: pointer;
    transition: color var(--t-fast), border-color var(--t-fast), background var(--t-fast);
  }
  .ing-btn:hover { color: var(--accent); border-color: var(--accent); }
  .ing-btn-primary { color: var(--accent); border-color: var(--accent); }
  .ing-btn-primary:hover { background: var(--accent-faint); color: var(--accent-bright); }
  .ing-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  .ing-result {
    align-items: center;
    text-align: center;
    padding: var(--s-6) var(--s-3);
  }
  .ing-jobid {
    display: flex;
    align-items: center;
    gap: var(--s-2);
    margin: 0;
    font-size: 10px;
    color: var(--on-surface-faint);
  }
  .ing-jobid code {
    font-family: var(--font-mono);
    color: var(--on-surface-variant);
    user-select: all;
    word-break: break-all;
  }
  .ing-row-confirm {
    display: flex;
    align-items: center;
    gap: var(--s-2);
  }
  .ing-confirm-glyph { color: var(--accent); font-size: 24px; line-height: 1; }
  .ing-confirm-text {
    color: var(--accent-bright);
    font-size: 14px;
    letter-spacing: 0.12em;
    font-weight: 700;
  }
  .ing-hint {
    color: var(--on-surface-variant);
    font-size: 11px;
    line-height: 1.5;
    max-width: 380px;
    margin: 0;
  }
</style>
